import {EditorState, StateEffect, StateField, Transaction} from '@codemirror/state'
import {EditorView} from '@codemirror/view'

/** Effect that will be triggered when focus changes.*/
const focusChangeEffect = StateEffect.define<boolean>()

// The idiomatic way would be to attach the effect to the `EditorView.focusChangeEffect` facet, but that doesn't seem
// to work as reliably - it doesn't fire when the editor refocuses after focus leaves the page entirely
const updateObserver = EditorView.updateListener.of(update => {
  // Similarly it would be more idiomatic to use `update.focusChanged`, but this is more reliable as it ensures the
  // state gets updated if it ever gets out of sync
  if (update.view.hasFocus !== isFocused(update.state)) {
    update.view.dispatch({
      effects: focusChangeEffect.of(update.view.hasFocus),
    })
  }
})

/** Field to store the focus state on the editor state. */
const isFocusedField = StateField.define<boolean>({
  create: () => false,
  update: (prev, tr) => tr.effects.find(eff => eff.is(focusChangeEffect))?.value ?? prev,
  provide: () => [updateObserver],
})

/**
 * While the focus state of the editor can be determined from `view.hasFocus`, a `StateField` plugin would not be
 * provided the `EditorView` (because state is decoupled from UI). So this plugin mirrors the editor's UI focus state
 * onto the editor state, and triggers an update effect when that changes. This is useful for other plugins that need
 * to know whether the editor is focused or not.
 */
export const focusedExtension = [isFocusedField]

/** Get the focus state from the editor state. Throws an error if `focusedExtension` is not installed. */
export function isFocused(state: EditorState) {
  return state.field(isFocusedField)
}

/** Check if the focus state changed in this transaction. */
export function focusChanged(transaction: Transaction) {
  return transaction.effects.some(eff => eff.is(focusChangeEffect))
}
